/**
 * MatchesContext - Isolated Matches Data Management
 *
 * Provides matches-related data in isolation to prevent unnecessary re-renders
 * when other tournament data (teams, players, locations) changes.
 *
 * Data Sources:
 * - Matches: Real-time subscriptions (onSnapshot) for instant match updates
 *
 * Performance Optimizations:
 * - Page Visibility API: Pauses real-time listeners when tab is hidden
 * - Isolated context: Components using only matches won't re-render on team/player updates
 */

"use client";

import { useTournamentMatches } from "@/hooks";
import { Match } from "@/types";
import React, { createContext, useContext, useMemo } from "react";

interface MatchesContextType {
  matches: Match[];
  loading: boolean;
  error: string | null;
}

const MatchesContext = createContext<MatchesContextType | undefined>(undefined);

export const useMatchesContext = () => {
  const context = useContext(MatchesContext);
  if (!context) {
    throw new Error("useMatchesContext must be used within a MatchesProvider");
  }
  return context;
};

export function MatchesProvider({
  children,
  tournamentId,
}: {
  children: React.ReactNode;
  tournamentId: string;
}) {
  // ===========================================================================
  // REAL-TIME MATCHES DATA
  // ===========================================================================
  // Fetch matches with real-time updates using onSnapshot
  // Matches should update instantly when challenges are sent/accepted
  // ===========================================================================

  const {
    data: matches = [],
    isLoading: matchesLoading,
    error: matchesError,
  } = useTournamentMatches(tournamentId);

  const value = useMemo<MatchesContextType>(
    () => ({
      matches,
      loading: matchesLoading,
      error: matchesError?.message || null,
    }),
    [matches, matchesLoading, matchesError],
  );

  return (
    <MatchesContext.Provider value={value}>{children}</MatchesContext.Provider>
  );
}
